import { useMemo, useState } from "react";
import { MessageSquare, ChevronDown, ChevronRight } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useRightSidebar } from "@/contexts/RightSidebarContext";
import { useComments } from "@/hooks/useComments";
import { CommentItem } from "@/components/comments/CommentItem";
import { CommentInput } from "@/components/comments/CommentInput";

export function CommentsPanel() {
  const { currentEntity } = useRightSidebar();
  const { data: comments, isLoading } = useComments(
    currentEntity?.type,
    currentEntity?.id,
  );
  const [showResolved, setShowResolved] = useState(false);

  const { unresolvedComments, resolvedComments } = useMemo(() => {
    const unresolved = (comments || []).filter((c) => !c.is_resolved);
    const resolved = (comments || []).filter((c) => c.is_resolved);
    return { unresolvedComments: unresolved, resolvedComments: resolved };
  }, [comments]);

  if (!currentEntity) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
        <MessageSquare className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">
          Navigate to a page to see comments
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading comments...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {currentEntity.label && (
        <div className="px-3 py-2 border-b">
          <p className="text-xs text-muted-foreground truncate">
            {currentEntity.label}
          </p>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="px-3 py-1">
          {unresolvedComments.length === 0 && resolvedComments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No comments yet. Start the conversation!
            </p>
          ) : (
            <>
              {unresolvedComments.map((comment) => (
                <div key={comment.id}>
                  <CommentItem comment={comment} />
                  <Separator />
                </div>
              ))}

              {resolvedComments.length > 0 && (
                <div className="mt-2">
                  <button
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground py-1.5 w-full"
                    onClick={() => setShowResolved(!showResolved)}
                  >
                    {showResolved ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    {resolvedComments.length} resolved
                  </button>

                  {showResolved &&
                    resolvedComments.map((comment) => (
                      <div key={comment.id}>
                        <CommentItem comment={comment} />
                        <Separator />
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      <div className="px-3 pb-3">
        <CommentInput
          entityType={currentEntity.type}
          entityId={currentEntity.id}
        />
      </div>
    </div>
  );
}

export function useUnresolvedCommentCount() {
  const { currentEntity } = useRightSidebar();
  const { data: comments } = useComments(
    currentEntity?.type,
    currentEntity?.id,
  );

  return useMemo(
    () => (comments || []).filter((c) => !c.is_resolved).length,
    [comments],
  );
}
